import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import LoadingSpinner from "../components/LoadingSpinner";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { apiService } from "../services/api";

// Matches Android VALIDATION_CONSTANTS
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
// Android accepts 6 OR 8 char referral codes
const REFERRAL_CODE_REGEX = /^[A-Z0-9]{6}$|^[A-Z0-9]{8}$/;


const currentYear = new Date().getFullYear();
const minYear = currentYear - 100;
const maxYear = currentYear - 18;

const PANDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [dobError, setDobError] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    dateOfBirth: "",
    panNumber: "",
    referralCode: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "fullName") {
      const formatted = value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else if (name === "panNumber") {
      const formatted = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else if (name === "referralCode") {
      const formatted = value
        .replace(/[^a-zA-Z0-9]/g, "")
        .toUpperCase()
        .slice(0, 8);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const assembleDob = (d: string, m: string, y: string) => {
    const day = parseInt(d, 10);
    const month = parseInt(m, 10);
    const year = parseInt(y, 10);
    if (!d || !m || !y || d.length < 1 || m.length < 1 || y.length < 4) return "";
    if (isNaN(day) || isNaN(month) || isNaN(year)) return "";
    if (day < 1 || day > 31 || month < 1 || month > 12) return "";
    if (year < minYear || year > maxYear) return "";
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return "";
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  const handleDobChange = (
    field: "day" | "month" | "year",
    raw: string,
    nextId?: string
  ) => {
    const digits = raw.replace(/\D/g, "");
    const maxLen = field === "year" ? 4 : 2;
    const val = digits.slice(0, maxLen);

    let newDay = dobDay, newMonth = dobMonth, newYear = dobYear;
    if (field === "day") newDay = val;
    else if (field === "month") newMonth = val;
    else newYear = val;

    setDobDay(newDay);
    setDobMonth(newMonth);
    setDobYear(newYear);

    const assembled = assembleDob(newDay, newMonth, newYear);
    setFormData((prev) => ({ ...prev, dateOfBirth: assembled }));

    if (assembled === "" && newDay && newMonth && newYear.length === 4) {
      setDobError("Please enter a valid date of birth (age must be 18–100 years)");
    } else {
      setDobError("");
    }

    if (val.length === maxLen && nextId) {
      document.getElementById(nextId)?.focus();
    }
  };

  const isPanValid = () =>
    formData.panNumber.length === 0 || PAN_REGEX.test(formData.panNumber);

  const isReferralValid = () =>
    formData.referralCode.length === 0 ||
    REFERRAL_CODE_REGEX.test(formData.referralCode);

  const isFormValid = () =>
    formData.fullName.trim().length >= 2 &&
    !!formData.dateOfBirth &&
    isPanValid() &&
    isReferralValid();

  const validateForm = (): boolean => {
    if (formData.fullName.trim().length < 2) {
      toast.error("Please enter your full name as per PAN");
      return false;
    }
    if (!formData.dateOfBirth) {
      toast.error("Please select your date of birth");
      return false;
    }
    if (formData.panNumber && !PAN_REGEX.test(formData.panNumber)) {
      toast.error("Please enter a valid PAN number (e.g. ABCDE1234F)");
      return false;
    }
    if (
      formData.referralCode &&
      !REFERRAL_CODE_REGEX.test(formData.referralCode)
    ) {
      toast.error("Referral code must be exactly 6 or 8 characters");
      return false;
    }
    return true;
  };

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const requestData = {
      pan_number: formData.panNumber || null,
      full_name: formData.fullName,
      date_of_birth: formData.dateOfBirth,
      referral_code: formData.referralCode || null,
      empanelment_completed: true,
    };

    setIsLoading(true);
    try {
      const response = await apiService.put("/auth/profile/", requestData);

      // Mirror Android: check response.data.success before proceeding.
      // Backend may return 200 with success:false on validation errors.
      if (response.data?.success === false) {
        toast.error(
          response.data?.message ||
          response.data?.error ||
          "Failed to save details. Please check the information and try again."
        );
        return;
      }

      // Patch full payload locally — mirrors Android updateUser(requestData).
      // Backend doesn't persist empanelment_completed via profile PUT,
      // so we must set it in local state to unblock the routing guard in App.tsx.
      updateUser({
        pan_number: requestData.pan_number ?? undefined,
        full_name: requestData.full_name,
        date_of_birth: requestData.date_of_birth,
        referral_code: requestData.referral_code ?? undefined,
        empanelment_completed: true,
      });
      toast.success("Details saved successfully!");
      navigate("/");
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to save details. Please check the information and try again.";
      toast.error(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          disabled={isLoading}
        >
          <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
        </button>
        <h1 className="ml-4 text-xl font-semibold text-gray-900">
          Enter your details
        </h1>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <form onSubmit={handleNext} className="max-w-sm mx-auto space-y-6">
          {/* Full Name */}
          <div>
            <label
              htmlFor="fullName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Full Name as per PAN<span className="text-red-500">*</span>
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              required
              className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400"
              placeholder="KRISHNAKANT RAWAT"
              value={formData.fullName}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth<span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                id="dob-day"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="DD"
                maxLength={2}
                value={dobDay}
                onChange={(e) => handleDobChange("day", e.target.value, "dob-month")}
                disabled={isLoading}
                className="w-16 px-3 py-4 text-base text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <span className="text-gray-400 text-lg">/</span>
              <input
                id="dob-month"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="MM"
                maxLength={2}
                value={dobMonth}
                onChange={(e) => handleDobChange("month", e.target.value, "dob-year")}
                disabled={isLoading}
                className="w-16 px-3 py-4 text-base text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <span className="text-gray-400 text-lg">/</span>
              <input
                id="dob-year"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="YYYY"
                maxLength={4}
                value={dobYear}
                onChange={(e) => handleDobChange("year", e.target.value)}
                disabled={isLoading}
                className="flex-1 px-3 py-4 text-base text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            {dobError && (
              <p className="mt-1 text-xs text-red-600">{dobError}</p>
            )}
          </div>

          {/* PAN Number — Optional */}
          <div>
            <label
              htmlFor="panNumber"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              PAN Number{" "}
              <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              id="panNumber"
              name="panNumber"
              type="text"
              className={`w-full px-4 py-4 text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 ${
                formData.panNumber && !isPanValid()
                  ? "border-red-400 bg-red-50"
                  : "border-gray-300"
              }`}
              placeholder="ABCDE1234F"
              value={formData.panNumber}
              onChange={handleInputChange}
              disabled={isLoading}
              maxLength={10}
            />
            {formData.panNumber && !isPanValid() && (
              <p className="mt-1 text-xs text-red-600">
                Format: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F)
              </p>
            )}
          </div>

          {/* Referral Code — Optional, 6 or 8 chars */}
          <div>
            <label
              htmlFor="referralCode"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Referral Code{" "}
              <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              id="referralCode"
              name="referralCode"
              type="text"
              className={`w-full px-4 py-4 text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 ${
                formData.referralCode && !isReferralValid()
                  ? "border-red-400 bg-red-50"
                  : "border-gray-300"
              }`}
              placeholder="6 or 8 digit code"
              value={formData.referralCode}
              onChange={handleInputChange}
              disabled={isLoading}
              maxLength={8}
            />
            {formData.referralCode && !isReferralValid() && (
              <p className="mt-1 text-xs text-red-600">
                Must be exactly 6 or 8 characters
              </p>
            )}
          </div>
        </form>
      </div>

      {/* Bottom Section */}
      <div className="px-6 pb-8">
        <div className="max-w-sm mx-auto space-y-4">
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            By clicking Next, I provide consent to Varicash Terms and Conditions
          </p>

          <button
            onClick={handleNext}
            disabled={isLoading || !isFormValid()}
            className={`w-full text-white font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center text-base disabled:cursor-not-allowed ${
              isFormValid() && !isLoading
                ? "bg-primary-600 hover:bg-primary-700"
                : "bg-gray-300"
            }`}
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PANDetailsPage;
